import React, { FC, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer } from "@mui/material";

import { ReactComponent as Logo } from "../../assets/img/logo.svg";

import Nav, { Link as NavLink } from "./Nav";
import NavItem from "./NavItem";
import NavDivider from "./NavDivider";
import { AuthContext } from "../../contexts/AuthContext";
import { ReactComponent as LogoMobile } from "../../assets/img/logo_mobile.svg";
import {
    AccountIcon,
    AddIcon,
    AdminPayoutIcon,
    GraduationCapIcon,
    LogoutIcon,
    MemberIcon,
    MenuIcon,
    PayoutsIcon,
    SettingsIcon,
    StatsIcon,
    TradesIcon,
} from "../../assets/img/icons";
import AssistanceBox from "./AssistanceBox";
import Button from "../Button";

import styles from "./styles.module.scss";
import TradingMethodModal from "./TradingMethodModal";
import { logout } from "../../services/authService";
import Switch from "../Switch";
import Text from "../Text";
import { Role } from "../../types/enums/Role";
import { canAccessOwnProgression, isAdminRole } from "../../utils/graduation";
import { useUnsavedChangesOptional } from "../../contexts/UnsavedChangesContext";

interface Props {
    children: React.ReactNode;
    pageContentClassName?: string;
}

const buildAdminNavItems = (sectionClassName?: string): NavLink[] => [
    {
        label: "Listing",
        className: sectionClassName,
        subItems: [
            {
                to: "/admin/members",
                label: "Membres",
                icon: MemberIcon,
                resource: "admin-members",
            },
            {
                to: "/admin/payouts",
                label: "Payouts",
                icon: AdminPayoutIcon,
                resource: "admin-payouts",
            },
        ],
    },
    {
        label: "Pédagogies",
        className: sectionClassName,
        subItems: [
            {
                to: "/admin/promotions",
                label: "Promotions",
                icon: GraduationCapIcon,
                resource: "admin-promotions",
            },
            {
                to: "/admin/graduation",
                label: "Apprentissage",
                icon: GraduationCapIcon,
                resource: ["admin-graduation", "admin-formateur-graduation"],
            },
        ],
    },
    {
        label: "Autres",
        className: sectionClassName,
        subItems: [
            {
                to: "/admin/stats",
                label: "Statistiques",
                icon: StatsIcon,
                resource: "admin-stats",
            },
            {
                to: "/admin/settings",
                label: "Paramètres",
                icon: SettingsIcon,
                resource: "admin-tags",
            },
        ],
    },
];

const ConnectedLayout: FC<Props> = ({ children, pageContentClassName }) => {
    const navigate = useNavigate();
    const unsavedChanges = useUnsavedChangesOptional();
    const { hasPermission, isStreamOn, handleIsStreamOn, user } =
        useContext(AuthContext)!;

    const goTo = (path: string) => {
        if (unsavedChanges) {
            unsavedChanges.requestNavigate(path);
            return;
        }
        navigate(path);
    };

    const handleLogout = () => {
        if (unsavedChanges) {
            unsavedChanges.requestLogout();
            return;
        }
        void logout();
    };

    const [open, setOpen] = useState(false);

    const toggleDrawer = (newOpen: boolean) => () => {
        setOpen(newOpen);
    };

    const [isTradingMethodModalOpen, setIsTradingMethodModalOpen] =
        useState(false);

    const canUseStreamMode =
        isAdminRole(user?.role) || user?.role === Role.Formateur;

    const dashboardSubItems = useMemo(
        (): NavLink[] => [
            {
                to: "/trades",
                label: "Trades",
                icon: TradesIcon,
                resource: "trading-session",
            },
            {
                to: "/payouts",
                label: "Payouts",
                icon: PayoutsIcon,
                resource: "payout",
            },
            ...(canAccessOwnProgression(user?.role, user?.promotionId)
                ? [
                      {
                          to: "/formation",
                          label: "Progression",
                          icon: GraduationCapIcon,
                      } satisfies NavLink,
                  ]
                : []),
        ],
        [user?.role, user?.promotionId]
    );

    return (
        <div className={`${styles.container}`}>
            <TradingMethodModal
                open={isTradingMethodModalOpen}
                onClose={() => setIsTradingMethodModalOpen(false)}
            />
            <div className={styles.navbar}>
                <div>
                    <Logo
                        className={styles.logo}
                        onClick={() => goTo("/trades")}
                    />
                    <Button
                        className={styles.tradingMethodButton}
                        onClick={() => setIsTradingMethodModalOpen(true)}
                        label="Notice"
                        size="small"
                    />
                    <Nav
                        navItems={[
                            {
                                label: "Tableau de bord",
                                resource: "trading-session",
                                subItems: dashboardSubItems,
                            },
                            ...buildAdminNavItems(),
                        ]}
                    />
                </div>
                <div className={styles.footer}>
                    {canUseStreamMode && (
                        <div className={styles.switch_stream}>
                            <Text
                                variant="body-bold"
                                className={styles.switch_stream_label}
                            >
                                Mode diffusion
                            </Text>
                            <Switch
                                key={"trading-method"}
                                checked={isStreamOn}
                                onChange={() => handleIsStreamOn(!isStreamOn)}
                                colors={{
                                    on: "#00B69C",
                                    off: "#ccc",
                                    thumb: "#fff",
                                }}
                            />
                        </div>
                    )}
                    {hasPermission("invite", "create") && (
                        <div className={styles.footer_links}>
                            <NavItem
                                className={styles.footer_item}
                                to={"/invite"}
                                label={"Invitations"}
                                Icon={AddIcon}
                            />
                        </div>
                    )}
                    <AssistanceBox />
                    <NavDivider />
                    <p className={styles.legal_notices}>
                        <a
                            href="https://www.objectif-trading.fr/mentions-legales"
                            rel="noreferrer"
                            target="_blank"
                        >
                            Mentions Légales
                        </a>
                        {" – "}
                        <a
                            href="https://www.objectif-trading.fr/cgu"
                            rel="noreferrer"
                            target="_blank"
                        >
                            CGU
                        </a>
                    </p>
                </div>
            </div>
            <div className={styles.mobile}>
                <div className={styles.mobile_header_left}>
                    <LogoMobile onClick={() => goTo("/trades")} />
                    <Button
                        className={styles.tradingMethodButton}
                        onClick={() => setIsTradingMethodModalOpen(true)}
                        label="Notice"
                        size="small"
                    />
                </div>
                <div onClick={toggleDrawer(true)}>
                    <MenuIcon width={40} color="white" />
                </div>
                <Drawer
                    className={styles.drawer}
                    anchor="right"
                    open={open}
                    onClose={toggleDrawer(false)}
                    disableScrollLock
                >
                    <div className={styles.drawer_container}>
                        <div className={styles.drawer_content}>
                            <Nav
                                showSubItemDividers
                                onClose={toggleDrawer(false)}
                                closeOnSectionLabel="Tableau de bord"
                                closeButtonClassName={styles.drawer_close}
                                sectionHeaderClassName={styles.drawer_section_header}
                                navItems={[
                                    {
                                        label: "Tableau de bord",
                                        resource: "trading-session",
                                        className: styles.drawer_section_title,
                                        subItems: dashboardSubItems,
                                    },
                                    ...buildAdminNavItems(
                                        styles.drawer_section_title
                                    ),
                                    {
                                        to: "/account",
                                        label: "Mon compte",
                                        icon: AccountIcon,
                                    },
                                ]}
                            />
                            {canUseStreamMode && (
                                <div className={styles.switch_stream_mobile}>
                                    <Text
                                        variant="body-bold"
                                        className={styles.switch_stream_label}
                                    >
                                        Mode diffusion
                                    </Text>
                                    <Switch
                                        key={"mobile-trading-method"}
                                        checked={isStreamOn}
                                        onChange={() =>
                                            handleIsStreamOn(!isStreamOn)
                                        }
                                        colors={{
                                            on: "#00B69C",
                                            off: "#ccc",
                                            thumb: "#fff",
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className={`${styles.footer} ${styles.drawer_footer}`}
                        >
                            <AssistanceBox />
                            <div className={styles.drawer_logout}>
                                <LogoutIcon />
                                <p
                                    className={styles.drawer_logout_link}
                                    onClick={handleLogout}
                                >
                                    Se déconnecter
                                </p>
                            </div>
                            <NavDivider />
                            <p className={styles.legal_notices}>
                                <a
                                    href="https://www.objectif-trading.fr/mentions-legales"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Mentions Légales
                                </a>
                                {" – "}
                                <a
                                    href="https://www.objectif-trading.fr/cgu"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    CGU
                                </a>
                            </p>
                        </div>
                    </div>
                </Drawer>
            </div>
            <div
                className={`${styles.page_content} ${
                    pageContentClassName ? pageContentClassName : ""
                }`}
            >
                {children}
            </div>
        </div>
    );
};

export default ConnectedLayout;
